import React from 'react';
import _ from 'underscore';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { titleize } from 'underscore.string';
import Badge from 'components/v2/Badge';
import { Card } from 'components/v2/stats';

const getCalendarLabel = (date) => (
  <div>
    <span className="uppercase">{`${moment(date)
      .format('ddd')
      .substring(0, 3)},`}</span>
    <span className="uppercase px-1 ">
      {moment(date).format('MMM').substring(0, 3)}
    </span>
    <span className="font-bold">{moment(date).date()}</span>
  </div>
);

export default function AppointmentCalendarMetrics({
  onCalendarClick,
  selectedDate,
  metrics,
}) {
  const { t } = useTranslation();

  const TODAY = moment().startOf('day');
  let date = TODAY;
  const MAX_DAYS = 6;

  function isSelected(compare) {
    return (
      selectedDate &&
      moment(selectedDate).startOf('day').isSame(moment(compare).startOf('day'))
    );
  }

  function getMetrics(date) {
    const items = metrics.filter((m) => {
      return moment(m.date).startOf('day').isSame(moment(date).startOf('day'));
    });

    return items.length ? _.first(items).data : {};
  }

  const yesterday = moment().subtract(1, 'days').startOf('day');
  const today = moment().startOf('day');

  const components = [
    <AppointmentCalendarMetric
      key="yesterday"
      label={t('YESTERDAY')}
      className="flex-1 mb-2 sm:mb-0 flex "
      date={yesterday}
      metrics={getMetrics(yesterday)}
      onCalendarClick={onCalendarClick}
      selected={isSelected(yesterday)}
    />,
    <AppointmentCalendarMetric
      key="today"
      label={t('TODAY')}
      className="flex-1 mb-2 sm:mb-0 flex "
      date={today}
      metrics={getMetrics(today)}
      onCalendarClick={onCalendarClick}
      selected={isSelected(today)}
    />,
  ];

  for (let i = 0; i < MAX_DAYS; i++) {
    date = moment(date).add(1, 'days');
    components.push(
      <AppointmentCalendarMetric
        key={`date-${i}`}
        className="flex-1 mb-2 sm:mb-0 flex "
        date={date}
        label={getCalendarLabel(date)}
        metrics={getMetrics(date)}
        onCalendarClick={onCalendarClick}
        selected={isSelected(date)}
      />,
    );
  }

  return components;
}

function AppointmentCalendarMetric({
  label,
  date,
  metrics,
  selected,
  onCalendarClick,
}) {
  const start = moment(date).startOf('day').toISOString();
  const end = moment(date).endOf('day').toISOString();
  const { t } = useTranslation();
  const cardClassNames = classnames({
    'mt-2': true,
    'cursor-pointer': true,
    border: true,
    'border-green': selected,
    'border-solid': true,
    'hover:border': true,
    'hover:border-8': true,
    'hover:border-green': true,
    'hover:border-solid': true,
  });

  if (_.isEmpty(metrics)) {
    return (
      <Card
        className={cardClassNames}
        label={label}
        onClick={() => onCalendarClick({ start, end })}
      >
        <div>{t('NO_APPTS')}</div>
      </Card>
    );
  }

  function onCalendarStatusClick(e, data) {
    e.stopPropagation();
    onCalendarClick(data);
  }

  return (
    <Card
      label={label}
      className={cardClassNames}
      onClick={() => onCalendarClick({ start, end })}
    >
      <div className="mt-2 space-y-1">
        <div className="flex space-x-2 justify-between">
          <span>{titleize(t('APPROVED'))}</span>
          <div
            onClick={(e) =>
              onCalendarStatusClick(e, { status: 'approved', start, end })
            }
          >
            <Badge color="green">{metrics.approved}</Badge>
          </div>
        </div>
        <div className="flex space-x-2 justify-between">
          <span>{titleize(t('PENDING'))}</span>
          <div
            onClick={(e) =>
              onCalendarStatusClick(e, { status: 'pending', start, end })
            }
          >
            <Badge color="blue">{metrics.pending}</Badge>
          </div>
        </div>
        <div className="flex space-x-2 justify-between">
          <span>{titleize(t('DECLINED'))}</span>
          <div
            onClick={(e) =>
              onCalendarStatusClick(e, { status: 'declined', start, end })
            }
          >
            <Badge color="red">{metrics.declined}</Badge>
          </div>
        </div>
        <div className="flex space-x-2 justify-between">
          <span>{titleize(t('MISSED'))}</span>
          <div
            onClick={(e) =>
              onCalendarStatusClick(e, { status: 'missed', start, end })
            }
          >
            <Badge color="yellow">{metrics.missed}</Badge>
          </div>
        </div>
        <div className="flex space-x-2 justify-between">
          <span>{titleize(t('COMPLETE'))}</span>
          <div
            onClick={(e) =>
              onCalendarStatusClick(e, { status: 'complete', start, end })
            }
          >
            <Badge color="purple">{metrics.complete}</Badge>
          </div>
        </div>
        <div className="flex space-x-2 justify-between">
          <span>{titleize(t('CANCELED'))}</span>
          <div
            onClick={(e) =>
              onCalendarStatusClick(e, { status: 'canceled', start, end })
            }
          >
            <Badge color="gray">{metrics.canceled}</Badge>
          </div>
        </div>
      </div>
    </Card>
  );
}
